import React from 'react';
import { motion } from 'framer-motion';
import { AtSign, BookOpen, Code2, ExternalLink, Globe, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';

const Footer: React.FC = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-50 via-white to-blue-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-blue-950/20 border-t border-border/50 dark:border-gray-700/50 mt-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gradient-to-r from-purple-500/10 to-primary/10 rounded-full blur-3xl" />
      
      <div className="relative z-10">
        <motion.div 
          className="max-w-7xl mx-auto py-16 px-6 md:px-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Section */}
            <motion.div variants={fadeInUp} className="lg:col-span-1 space-y-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-primary/10 dark:bg-primary/20">
                  <BookOpen className="h-6 w-6 text-primary" />
                </div>
                <span className="font-bold text-xl tracking-tight bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                  SMCHS Library
                </span>
              </div>
              
              <p className="text-muted-foreground leading-relaxed max-w-sm">
                The library at St Mary's Catholic High School, Dubai. Books, databases and a desk to work at.
              </p>
              
              {/* Social Links */}
              <div className="flex space-x-3">
                {[
                  { icon: Globe, href: "#", label: "Website" },
                  { icon: MessageCircle, href: "#", label: "Updates" },
                  { icon: AtSign, href: "#", label: "Contact" },
                  { icon: Code2, href: "#", label: "Repository" }
                ].map((social, index) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-primary/10 dark:hover:bg-primary/20 text-muted-foreground hover:text-primary transition-all duration-200 group"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <h3 className="font-semibold text-foreground text-lg">Around the site</h3>
              <div className="space-y-3">
                {[
                  { name: "Browse the catalog", href: "/catalog" },
                  { name: "Book clubs", href: "/book-clubs" },
                  { name: "Reading challenges", href: "/challenges" },
                  { name: "Resources", href: "/resources" },
                  { name: "About the library", href: "/about" }
                ].map((link) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    className="block text-muted-foreground hover:text-primary transition-colors duration-200 text-sm group"
                    whileHover={{ x: 4 }}
                  >
                    <span className="flex items-center space-x-2">
                      <span>{link.name}</span>
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </span>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Services */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <h3 className="font-semibold text-foreground text-lg">Your account</h3>
              <div className="space-y-3">
                {[
                  { name: "Sign in", href: "/signin" },
                  { name: "Create an account", href: "/signup" },
                  { name: "Reset your password", href: "/reset-password" },
                  { name: "My desk", href: "/student" },
                  { name: "Librarian dashboard", href: "/librarian" }
                ].map((service) => (
                  <motion.a
                    key={service.name}
                    href={service.href}
                    className="block text-muted-foreground hover:text-primary transition-colors duration-200 text-sm group"
                    whileHover={{ x: 4 }}
                  >
                    <span className="flex items-center space-x-2">
                      <span>{service.name}</span>
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </span>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <h3 className="font-semibold text-foreground text-lg">Contact</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="p-1.5 rounded-md bg-primary/10 dark:bg-primary/20 mt-0.5">
                    <MapPin className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">SMCHS Library</p>
                    <p>St Mary's Catholic High School</p>
                    <p>Muhaisnah 4, Dubai</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 rounded-md bg-primary/10 dark:bg-primary/20">
                    <Phone className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-sm">
                    <p className="text-muted-foreground">+971 4 288 6700</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 rounded-md bg-primary/10 dark:bg-primary/20">
                    <Mail className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-sm">
                    <p className="text-muted-foreground">library@smchs.ae</p>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="space-y-2">
                <h4 className="font-medium text-foreground text-sm">Opening hours</h4>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Sunday to Thursday: 7:30 AM – 4:00 PM</p>
                  <p>Friday: 7:30 AM – 12:00 PM</p>
                  <p>Saturday: closed</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Section */}
          <motion.div 
            variants={fadeInUp}
            className="mt-16 pt-8 border-t border-border/50 dark:border-gray-700/50"
          >
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-sm text-muted-foreground">
                <p>&copy; {new Date().getFullYear()} SMCHS Library.</p>
              </div>
              
              <div className="flex space-x-6 text-sm">
                {[
                  { name: "Privacy Policy", href: "/privacy" },
                  { name: "Terms of Service", href: "/terms" },
                  { name: "Accessibility", href: "/accessibility" }
                ].map((link, index) => (
                  <React.Fragment key={link.name}>
                    <motion.a
                      href={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors duration-200"
                      whileHover={{ scale: 1.05 }}
                    >
                      {link.name}
                    </motion.a>
                    {index < 2 && <span className="text-muted-foreground/50">•</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
